import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import assetsService from "../../services/rentals/assetsService";
import EntityList from "../../components/EntityList";
import {
  Box,
  HStack,
  Button,
  SimpleGrid,
  Image,
  Heading,
  Text,
  Badge,
  VStack,
  Flex,
  AspectRatio,
  IconButton,
  Tooltip,
  Stack,
  Spacer,
} from "@chakra-ui/react";
import { EditIcon, InfoOutlineIcon } from "@chakra-ui/icons";

export default function AssetsList() {
  const [assets, setAssets] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("rentals.assets.view") || "table";
    } catch (e) {
      return "table";
    }
  }); // 'table' or 'grid'
  function handleViewChange(mode) {
    try {
      localStorage.setItem("rentals.assets.view", mode);
    } catch (e) {
      // ignore
    }
    setViewMode(mode);
  }
  const navigate = useNavigate();

  async function load() {
    try {
      const res = await assetsService.listAssets();
      setAssets(res.items || res || []);
    } catch (e) {
      console.error("failed loading assets", e);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const columns = [
    {
      key: "asset_id",
      label: "Asset ID",
      render: (a) => (
        <span style={{ fontFamily: "monospace", color: "#4a5568" }}>
          {a.asset_id}
        </span>
      ),
    },
    {
      key: "vehicle",
      label: "Vehicle",
      render: (a) => (
        <div>
          <div style={{ fontWeight: 600 }}>{`${a.make} ${a.model} ${
            a.year || ""
          }`}</div>
          <div style={{ fontSize: 12, color: "#718096" }}>
            {a.vin || a.license_plate}
          </div>
        </div>
      ),
    },
    { key: "category", label: "Category" },
    { key: "availability_status", label: "Status" },
    {
      key: "base_location",
      label: "Base",
      render: (a) => a.base_location || "-",
    },
    {
      key: "daily_price",
      label: "Daily",
      render: (a) =>
        a.pricing && a.pricing.daily ? `$${a.pricing.daily}` : "-",
    },
    {
      key: "odometer",
      label: "Odometer",
      render: (a) =>
        typeof a.odometer === "number" ? a.odometer.toLocaleString() : "-",
    },
    {
      key: "location",
      label: "Location",
      render: (a) => {
        const coords = a.current_location && a.current_location.coordinates;
        if (!coords || coords.length < 2) return "-";
        const [lon, lat] = coords;
        return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: () => <span>⋮</span>,
      align: "right",
    },
  ];

  return (
    <Box>
      <Flex justify="space-between" mb={4} align="center">
        <Box>
          <Heading size="md">Assets</Heading>
          <Text fontSize="13px" color="gray.500">
            Manage rental assets
          </Text>
        </Box>
        <HStack>
          <Button
            size="sm"
            onClick={() => handleViewChange("table")}
            variant={viewMode === "table" ? "solid" : "ghost"}
          >
            List
          </Button>
          <Button
            size="sm"
            onClick={() => handleViewChange("grid")}
            variant={viewMode === "grid" ? "solid" : "ghost"}
          >
            Gallery
          </Button>
          <Button
            colorScheme="teal"
            size="sm"
            onClick={() => navigate("/rentals/assets/new")}
          >
            + New
          </Button>
        </HStack>
      </Flex>

      {viewMode === "table" ? (
        <EntityList
          title="Assets"
          subtitle="Manage rental assets"
          items={assets}
          columns={columns}
          onNew={() => navigate("/rentals/assets/new")}
          onRowClick={(a) => navigate(`/rentals/asset/${a.asset_id || a._id}`)}
        />
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
          {assets.map((a) => {
            const img =
              (a.images && a.images.length && a.images[0].url) ||
              "https://placehold.co/600x400?text=no+image";
            return (
              <Box
                key={a._id}
                borderWidth={1}
                borderRadius="8px"
                overflow="hidden"
                position="relative"
                onClick={() =>
                  navigate(`/rentals/asset/${a.asset_id || a._id}`)
                }
                _hover={{ boxShadow: "lg", transform: "translateY(-4px)" }}
                transition="all 150ms ease"
                cursor="pointer"
              >
                <AspectRatio ratio={16 / 9}>
                  <Image src={img} alt={a.asset_id} objectFit="cover" />
                </AspectRatio>

                <Box position="absolute" top={3} left={3}>
                  <Badge
                    colorScheme={
                      a.availability_status === "available" ? "green" : "orange"
                    }
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {a.availability_status}
                  </Badge>
                </Box>

                <IconButton
                  aria-label="Info"
                  icon={<InfoOutlineIcon />}
                  size="sm"
                  position="absolute"
                  top={2}
                  right={2}
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/rentals/asset/${a.asset_id || a._id}`);
                  }}
                />

                <VStack align="start" spacing={1} p={3}>
                  <HStack justify="space-between" width="100%">
                    <Heading size="sm">{`${a.make} ${a.model} ${
                      a.year || ""
                    }`}</Heading>
                    <Text fontSize="sm" color="gray.600" fontWeight={700}>
                      {a.pricing && a.pricing.daily
                        ? `$${a.pricing.daily}/day`
                        : "-"}
                    </Text>
                  </HStack>

                  <Text fontSize="12px" color="gray.500">
                    {a.vin || a.license_plate || a.asset_id}
                  </Text>

                  <HStack width="100%" justify="space-between" pt={2}>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/rentals/cart?assetId=${a.asset_id || a._id}`
                          );
                        }}
                      >
                        Reserve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/rentals/bookings?assetId=${a.asset_id || a._id}`
                          );
                        }}
                      >
                        Bookings
                      </Button>
                    </HStack>

                    <HStack spacing={1}>
                      <Tooltip label="Edit asset">
                        <IconButton
                          aria-label="Edit"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/rentals/assets/${a.asset_id || a._id}/edit`
                            );
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  );
}
